import React, { useState, useEffect } from 'react';
import './index.css'
import { CustomTable, Search, Pagination, Loader, ConfirmationPopup, SuccessModal, getIcon } from '@ohif/ui';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import addEditPackage from '@custom/rtk-apis/billing/addBilling';
import getPackage from '@custom/rtk-apis/billing/getBilling';
import getPrice from '@custom/rtk-apis/price/getPrice';
import { MenuItem, Select, ThemeProvider, createTheme } from '@mui/material';
import deletePackage from '@custom/rtk-apis/billing/deleteBilling';
import getInvestigationApi from '@custom/rtk-apis/billing/getInvestigation';
import editPackage from '@custom/rtk-apis/billing/editBilling';
import * as XLSX from 'xlsx';
import Tooltip from '@mui/material/Tooltip';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';
import { getCurrencySymbol } from '@custom/Utils/utils';
import { servicesManager } from '../../app/src/App';
import { useTheme } from '../../ui/src/contextProviders';

interface BillingPackageProps {
  activeTab: string;
  setActiveTab?: (tab: string) => void;
}

interface ModalityOption {
  label: string;
  value: string;
}

interface InvestigationPackage {
  "Package Name": string;
  "Services Included": string;
  "Price": number;
  "uniqueCode": string;
}

interface InvestigationResponse {
  status: string;
  msg: string;
  data: InvestigationPackage[];
}

interface ServicePackage {
  PACK_SERV_CODE: string;
  SERVICE_PACKAGE_NAME: string;
  PACAKGE_CODE: string;
  OPRBILMAK_SERVICE_CODE: string;
  OPRBILMAB_SERVICE_NAME: string;
  OPRBILMAD_COST: number;
}

interface GroupedServicePackage {
  SERVICE_PACKAGE_NAME: string;
  services: string[];
  OPRBILMAD_COST: number;
  PACK_SERV_CODE: string;
  PACAKGE_CODE: string;
}

const BillingPackage = (props: BillingPackageProps) => {
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch<AppDispatch>()
  const { result, loading, status } = useSelector((state: RootState) => state?.addEditPackageResponse);
  const packageData = useSelector((state: RootState) => state?.getPackageResponse?.result);
  const { result: pricingData, loading: pricingLoader, error } = useSelector((state: RootState) => state.getPriceSliceResponse);
  const { result: investigationData } = useSelector((state: RootState) => state.getInvestigationResponse);
  const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);
  const { uiNotificationService } = servicesManager.services;

  const navigate = useNavigate();
  const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');

  const [tableData, setTableData] = useState([]);
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [totalPg, setTotalPg] = useState(1000000);
  const [search, setSearch] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPackageForm, setShowPackageForm] = useState(false);
  const [modalityOptions, setModalityOptions] = useState<ModalityOption[]>([]);
  const [investigationOptions, setInvestigationOptions] = useState<ModalityOption[]>([]);
  const [editPackageData, setEditPackageData] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [currency, setCurrency] = useState("");

  // Create Material-UI theme based on dark mode
  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      ...(isDarkMode ? {
        background: {
          paper: '#2d2d2d',
          default: '#1a1a1a',
        },
        text: {
          primary: '#ffffff',
          secondary: '#b3b3b3',
        },
      } : {}),
    },
    components: {
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            color: '#000000',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#cccccc',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDarkMode ? '#777777' : '#999999',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: isDarkMode ? '#ADE8F4' : '#ADE8F4',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            color: '#000000',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
            '&.Mui-disabled': {
              backgroundColor: '#f5f5f5',
              color: '#666666',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            color: '#000000',
          },
        },
      },
    },
  });

  useEffect(() => {
    dispatch(getPrice({ modality: '', studyRegion: '' }));
    dispatch(getInvestigationApi());
    dispatch(getBillingGeneralSettings());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPackage({ search, page: pgNumber, limit: pgLimit }));
  }, [dispatch, search, pgNumber, pgLimit])

  useEffect(() => {
    if (billingGeneralSettingsData?.result?.currency) {
      setCurrency(billingGeneralSettingsData?.result?.currency);
    }
  }, [billingGeneralSettingsData]);

  const columns = [
    { key: 'name', title: 'Package Name' },
    { key: 'services', title: 'Services Including' },
    { key: 'pricing', title: 'Package Pricing', sort: true },
    { key: 'actions', title: 'Action' },
  ];

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      const headers = jsonData[0] as string[];
      const rows = jsonData.slice(1) as string[][];

      const parsedData: ServicePackage[] = rows
        .filter(row => row.length > 0)
        .map(row => ({
          PACK_SERV_CODE: row[headers.indexOf('PACK_SERV_CODE')] || '',
          SERVICE_PACKAGE_NAME: row[headers.indexOf('SERVICE_PACKAGE_NAME')] || '',
          PACAKGE_CODE: row[headers.indexOf('PACAKGE_CODE')] || '',
          OPRBILMAK_SERVICE_CODE: row[headers.indexOf('OPRBILMAK_SERVICE_CODE')] || '',
          OPRBILMAB_SERVICE_NAME: row[headers.indexOf('OPRBILMAB_SERVICE_NAME')] || '',
          OPRBILMAD_COST: parseFloat(row[headers.indexOf('OPRBILMAD_COST')] as string) || 0,
        }));

      const groupedData: { [key: string]: GroupedServicePackage } = {};
      parsedData.forEach(item => {
        if (!groupedData[item.SERVICE_PACKAGE_NAME]) {
          groupedData[item.SERVICE_PACKAGE_NAME] = {
            SERVICE_PACKAGE_NAME: item.SERVICE_PACKAGE_NAME,
            services: [item.OPRBILMAB_SERVICE_NAME],
            OPRBILMAD_COST: item.OPRBILMAD_COST,
            PACK_SERV_CODE: item.PACK_SERV_CODE,
            PACAKGE_CODE: item.PACAKGE_CODE
          };
        } else {
          groupedData[item.SERVICE_PACKAGE_NAME].services.push(item.OPRBILMAB_SERVICE_NAME);
        }
      });

      try {
        setIsSubmitting(true);
        const uploadPromises = Object.values(groupedData).map(async (item) => {
          const payload = {
            packageName: item.SERVICE_PACKAGE_NAME,
            servicesIncluded: item.services,
            packagePricing: item.OPRBILMAD_COST,
            hospitalId: hospitalId,
            mode: 'upload',
          };
          return dispatch(addEditPackage(payload));
        });

        await Promise.all(uploadPromises);
        const newTableData = Object.values(groupedData).map(item => ({
          id: `${item.PACK_SERV_CODE}-${Date.now()}`,
          name: capitalizeWords(item.SERVICE_PACKAGE_NAME),
          services: item.services.join(', '),
          pricing: item.OPRBILMAD_COST,
        }));

        setTableData(prevData => [...newTableData, ...prevData]);

        setMsg('Packages uploaded successfully.');
        setSuccess(true);
        dispatch(getPackage({ search, page: pgNumber, limit: pgLimit }));
      } catch (error) {
        console.error("Error uploading packages:", error);
        setMsg('Error uploading packages. Please try again.');
        setSuccess(true);
      } finally {
        setIsSubmitting(false);
      }
    };
    reader.onerror = () => {
      setMsg('Error reading Excel file.');
      setSuccess(true);
    };
    reader.readAsBinaryString(file);
  };

  const capitalizeWords = (str) => {
    if (!str) return '';

    let result = str.toLowerCase();
    result = result
      .split(' ')
      .map(word => {
        return word
          .split(/(?=[.-])/)
          .map(part => {
            if (part.length > 1 && /^[.-]/.test(part)) {
              return part.charAt(0) + part.charAt(1).toUpperCase() + part.slice(2);
            }
            return part.charAt(0).toUpperCase() + part.slice(1);
          })
          .join('');
      })
      .join(' ');
    return result;
  };

  const formatSentence = (str) => {
    if (!str) return '';

    let result = capitalizeWords(str);
    const uppercaseParentheses = (text) => {
      return text.replace(/\(([^)]+)\)/g, (_, content) => `(${content.toUpperCase()})`);
    };
    result = uppercaseParentheses(result);
    return result;
  };

  useEffect(() => {
    if (packageData?.data && Array.isArray(packageData?.data)) {
      const formattedData = packageData.data.map((item) => {
        let services = Array.isArray(item.servicesIncluded)
          ? item.servicesIncluded.join(', ')
          : item.servicesIncluded || '';

        let packageName = item.packageName || '';
        packageName = capitalizeWords(packageName);
        if (item.mode === 'upload') {
          services = formatSentence(services);
        }

        const servicesArray = services.split(', ').map(service => service.trim());
        const shortServices = servicesArray.slice(0, 2).join(', ') + (servicesArray.length > 2 ? ' ...' : '');
        const fullServices = servicesArray.join(', ');

        return {
          id: item?._id,
          name: packageName,
          services: servicesArray.length > 0 ? (
            <Tooltip title={fullServices} arrow>
              <span>{shortServices}</span>
            </Tooltip>
          ) : '',
          rawServices: servicesArray,
          pricing: `${getCurrencySymbol(currency)}${item.packagePricing || 0}`,
        };
      });

      const uniqueData = formattedData.reduce((acc, item) => {
        const itemKey = `${item.id}|${item.name}|${item.services.props?.children || ''}|${item.pricing}`;
        if (!acc.keys.has(itemKey)) {
          acc.keys.add(itemKey);
          acc.result.push(item);
        }
        return acc;
      }, { keys: new Set(), result: [] }).result;

      setTableData(uniqueData);
      if (packageData?.total) {
        setTotalPg(Math.ceil(packageData?.total));
      } else {
        setTotalPg(Math.ceil(uniqueData?.length));
      }
    }
  }, [packageData, currency, pgLimit]);

  useEffect(() => {
    if (investigationData?.data && Array.isArray(investigationData?.data)) {
      const typedData = investigationData as unknown as InvestigationResponse;

      if (typedData.data && Array.isArray(typedData.data)) {
        const packageOptions = typedData.data.map((item) => ({
          label: item["Package Name"],
          value: item["Package Name"],
          details: item
        }));
        setInvestigationOptions(packageOptions);
      }
    }
  }, [investigationData]);

  const handleAddPackage = () => {
    if (billingGeneralSettingsData?.msg === "General settings not found for this hospital.") {
      props.setActiveTab?.('general');
      uiNotificationService.show({
        title: 'Billing Settings',
        message: 'Fill the general settings to create appointment.',
        type: 'error',
      });
      navigate('/billing');
    } else {
      setEditPackageData(null);
      setShowPackageForm(true);
    }
  }

  useEffect(() => {
    if (Array.isArray(pricingData)) {
      const uniqueModalities = Array.from(new Set(pricingData.map(item => item.modality)))
        .filter(Boolean)
        .map(modality => ({
          label: modality,
          value: modality,
        }));

      setModalityOptions(uniqueModalities);
    }
  }, [pricingData]);

  const initialValues = {
    packageName: editPackageData?.name || '',
    services: editPackageData?.services || [],
    packagePrice: editPackageData?.pricing || '',
  };

  const submitForm = async (values) => {
    setIsSubmitting(true);

    const payload = {
      packageName: values.packageName,
      servicesIncluded: values.services,
      packagePricing: values.packagePrice,
      hospitalId: hospitalId,
      mode: editPackageData ? 'edit' : 'add',
    };

    try {
      if (editPackageData) {
        await dispatch(editPackage({
          packageId: editPackageData.id,
          ...payload
        }));
        setMsg('Package updated successfully.');
      } else {
        await dispatch(addEditPackage(payload));
        setMsg('Package added successfully.');
      }

      dispatch(getPackage({ search, page: pgNumber, limit: pgLimit }));
      setSuccess(true);
      setShowPackageForm(false);
    } catch (error) {
      console.error("Error processing package:", error);
      setMsg('An error occurred. Please try again.');
      setSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (selectedRow) {
      try {
        await dispatch(deletePackage({ id: selectedRow?.id }));
        dispatch(getPackage({ search, page: pgNumber, limit: pgLimit }));
        setSuccess(true);
        setMsg('Package deleted successfully.');
      } catch (error) {
        console.error("Error deleting package:", error);
      }
    }
    setIsPopupOpen(false);
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
  };

  const actions = [
    {
      id: 'editPackage',
      label: 'Edit',
      iconName: 'edit',
      onClick: (item) => {
        setEditPackageData({
          id: item.id,
          name: item.name,
          services: item.rawServices,
          pricing: item.pricing,
        });
        setShowPackageForm(true);
      }
    },
    {
      id: 'deletePackage',
      label: 'Delete',
      iconName: 'delete',
      onClick: (item) => {
        setSelectedRow(item);
        setIsPopupOpen(true);
      }
    },
  ];

  const searchClick = (searchText: string) => {
    setSearch(searchText);
    setSearchInputValue(searchText);
    setSearchQuery(searchText);
    setPgNumber(1);
    dispatch(getPackage({ search: searchText, page: 1, limit: pgLimit }));
  };

  const PackageForm = () => (
    <div className='details'>
      <div className='form form--innersec'>
        <div className="mb-3">
          <button
            className=''
            onClick={() => setShowPackageForm(false)}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {getIcon('icon-toolbar-back')}
            Back
          </button>
        </div>
        <section className='form__section'>
          <h1 className='form__section__title'>
            {editPackageData ? "Edit Package" : "Package Creation"}
          </h1>
          <Formik
            initialValues={initialValues}
            onSubmit={submitForm}
            enableReinitialize={true}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => {
              const isFormValid =
                values.packageName &&
                values.services.length > 0 &&
                values.packagePrice &&
                Number(values.packagePrice) > 0;

              return (
                <Form onSubmit={handleSubmit}>
                  <div className='formRow formRow__3'>
                    <div className='formCol'>
                      <div className='form__Field'>
                        <label className="form__label" htmlFor='packageName'>Package Name<span className="required-asterisk">*</span></label>
                        <input
                          type="text"
                          className='form__input form__input--sm'
                          id="packageName"
                          name="packageName"
                          placeholder="Package Name"
                          value={values.packageName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          autoComplete='off'
                        />
                      </div>
                    </div>

                    <div className='formCol'>
                      <div className='form__Field'>
                        <label className="form__label" htmlFor='services'>Add Services<span className="required-asterisk">*</span></label>
                        <ThemeProvider theme={muiTheme}>
                          <Select
                            id="services"
                            name="services"
                            multiple
                            value={values.services || []}
                            onChange={(event) => {
                              const value = event.target.value;
                              setFieldValue('services', typeof value === 'string' ? value.split(',') : value);
                            }}
                            onBlur={handleBlur}
                            className="form__select form__select--sm"
                            sx={{
                              '&.MuiOutlinedInput-root': {
                                border: '1px',
                                borderRadius: '2rem',
                              },
                            }}
                            renderValue={(selected) => (
                              Array.isArray(selected) ? selected.join(', ') : selected
                            )}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  backgroundColor: '#ffffff',
                                  color: '#000000',
                                },
                              },
                            }}
                          >
                            <MenuItem value="" disabled>Select Services</MenuItem>
                            <MenuItem disabled style={{
                              fontWeight: 'bold',
                              backgroundColor: isDarkMode ? '#404040' : '#f5f5f5',
                              color: isDarkMode ? '#888888' : '#666666'
                            }}>
                              Modalities
                            </MenuItem>
                            {modalityOptions.filter(option => !investigationOptions.some(io => io.value === option.value)).map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                style={{
                                  backgroundColor: values.services.includes(option.value)
                                    ? '#ADE8F4'
                                    : '#ffffff',
                                  color: values.services.includes(option.value)
                                    ? '#000000'
                                    : '#000000'
                                }}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                            <MenuItem disabled style={{
                              fontWeight: 'bold',
                              backgroundColor: isDarkMode ? '#404040' : '#f5f5f5',
                              color: isDarkMode ? '#888888' : '#666666'
                            }}>
                              Investigations
                            </MenuItem>
                            {investigationOptions.map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                style={{
                                  backgroundColor: values.services.includes(option.value)
                                    ? '#ADE8F4'
                                    : '#ffffff',
                                  color: values.services.includes(option.value)
                                    ? '#000000'
                                    : '#000000'
                                }}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </ThemeProvider>
                      </div>
                    </div>

                    <div className='formCol'>
                      <div className='form__Field'>
                        <label className="form__label" htmlFor='packagePrice'>Package Pricing<span className="required-asterisk">*</span></label>
                        <input
                          type="text"
                          className='form__input form__input--sm'
                          id="packagePrice"
                          name="packagePrice"
                          placeholder="Package Price"
                          value={values.packagePrice}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*\.?\d*$/.test(value) || value === '') {
                              setFieldValue("packagePrice", value);
                            }
                          }}
                          onBlur={handleBlur}
                          autoComplete='off'
                        />
                        {values.packagePrice && Number(values.packagePrice) <= 0 && (
                          <span style={{ color: 'red', fontSize: '12px' }}>
                            Price must be greater than 0
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='btn__group'>
                    <button className='btn btn__white' type="button" onClick={() => setShowPackageForm(false)}>
                      Cancel
                    </button>
                    <button className='btn btn__primary' type='submit' disabled={!isFormValid || isSubmitting}>
                      {editPackageData ? "Update" : "Save"}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </section>
      </div>
    </div>
  );

  return (
    <>
      <div className='page__body'>
        <div className='tableWrap'>
          {!showPackageForm ? (
            <>
              <div className='tableWrap__head'>
                <div className="formRow formRow__2 m_0 w_100 formRow--flexEnd">
                  <div className='formCol'>
                    <Search text="Search by Package Name" onSearchClick={searchClick} value={searchInputValue} setValue={setSearchInputValue} />
                  </div>
                </div>
                {props.activeTab === 'packageCreation' && (
                  <div className="btn__group">
                    <label htmlFor="fileUpload" className="btn btn__white" style={{ cursor: 'pointer' }}>
                      <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684" stroke="#150823" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Upload Package
                    </label>
                    <input id="fileUpload" type="file" accept=".xls,.xlsx" onChange={handleFileUpload} style={{ display: 'none' }} />
                    <button className="btn btn__white" onClick={handleAddPackage}>
                      <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684" stroke="#150823" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Add Package
                    </button>
                  </div>
                )}
              </div>
              <div className='tableContainer'>
                <CustomTable columns={columns} data={tableData} actions={actions} searchQuery={searchQuery} from='billingPackage' />
              </div>
            </>
          ) : (
            <PackageForm />
          )}
        </div>
        <SuccessModal isSuccessModalOpen={success} title={msg} handleCloseModal={() => setSuccess(false)} />
        <ConfirmationPopup isOpen={isPopupOpen} message={`Are you sure you want to delete ${selectedRow?.name}?`} onConfirm={handleDelete} onCancel={handleCancel} title={""} />
        <Loader isLoaderOpen={loading || !packageData || isSubmitting} />
      </div>
      {tableData.length > 0 && !showPackageForm && (
        <Pagination onChangePage={setPgNumber} onChangePerPage={setPgLimit} totalPage={totalPg} currentPage={pgNumber} perPage={pgLimit} />
      )}
    </>
  );
};

export default BillingPackage;
